
/**
 * The CacheableQueryProvider supports using SSR
 */

import { h, ComponentChildren, createContext } from 'preact'; /** @jsx h */
import { useContext, useState, useEffect } from 'preact/hooks';

import axios from 'axios';
import murmurHash3 from 'murmurhash3js';

import { useEnvironment } from './Environment';

const createHash = murmurHash3.x86.hash128;

export type ICacheableQueryProviderProps = {
    children?: ComponentChildren;
    

    onQuery?: any;

    /** are we in SSR mode at the server? */
    //isSsr: boolean = false;

    /** the content of the cache when rendering at the server */
    ssrCache?: any;
};


export type ICacheablePromise<T> = {
    method: string;

    endpoint: string;

    queryData?: T;

    apiIdentifier?: string;
}

export type ICacheableQuery<T> = ICacheablePromise<T> & {
    manual?: boolean;

    onLoad?: any | undefined;

    reloadOn?: Array<any> | undefined;

}

/**
 * The context for the CacheableQueryProvider
 */
export type ICacheableQueryProvider = {
    cacheableQuery?: (args: ICacheableQuery<any>) => any;
    cacheablePromise?: (args: ICacheablePromise<any>) => Promise<any>;
}

const CacheableQueryProviderContext = createContext<ICacheableQueryProvider>({});

export const useCacheableQueryProvider = () => {
    return useContext(CacheableQueryProviderContext);
};


export const CacheableQueryProvider = (props: ICacheableQueryProviderProps, hash?: string) => {
    const environment = useEnvironment();

    console.log('CacheableQueryProvider', environment);
    
    function cachablePromise<T>(args: ICacheablePromise<T>, hash?: string) {
        
        const queryPromise = axios({
            url: `${
                args.apiIdentifier && environment[args.apiIdentifier]
                    ? environment[args.apiIdentifier]
                    : environment.dataUrl
            }${args.endpoint}`,
            method: args.method,
            headers: {
                'Content-Type': 'application/json'
            },
            data: args.queryData
        });

        if (props.onQuery !== undefined) {
            props.onQuery(
                new Promise((resolve, reject) => {
                    queryPromise.then((response) => resolve({ hash, data: response?.data })).catch(reject);
                })
            )
            return Promise.resolve([{ data: undefined, loading: false, error: 'SSR' }]);
        } else {
            return queryPromise;
        }

    }

    // console.log('isDevelopment: ', isDevelopment);
    const parsedCache = (function () {
        try {
            /* if (!props.isSsr) {
                // console.log(window.__CACHE__)
                return window.__CACHE__;
            } */

            return props?.ssrCache !== undefined
                ? JSON.parse(props.ssrCache)
                : undefined;
        } catch (error) {
            return undefined;
        }
    })();

    function cacheableQuery<T>(args: ICacheableQuery<T>) {
        const hash = createHash?.(JSON.stringify(args));

        /**
         * in capture mode, we don't do anything
         */
        if (props.onQuery !== undefined) {
            cachablePromise(args, hash)
        }

        const item = parsedCache?.find(
            (i) => i?.hash.localeCompare(hash) === 0
        );

        const [result, setResult] = useState<{data: any, loading: boolean | undefined, error: any}>({
            data: item?.data,
            loading: undefined,
            error: undefined
        });
        const [cnt, setCnt] = useState(0);

        useEffect(
            () => {
                const fetchData = async () => {
                    if ((args.manual && cnt === 0) || result.loading) {
                        return;
                    }
                    setResult({
                        data: result.data,
                        loading: true,
                        error: false
                    });

                    cachablePromise(args)
                        .then((response) => {
                            if (args.onLoad !== undefined) {
                                args?.onLoad?.(response);
                            }
                            setResult({
                                data: response?.data,
                                loading: false,
                                error: false
                            });
                        })
                        .catch((err) => {
                            console.error(err);
                            setResult({
                                data: result.data,
                                loading: false,
                                error: err
                            });
                        });
                };
                fetchData();
            },
            [cnt].concat(
                args?.reloadOn !== undefined ? args.reloadOn : [] ?? []
            )
        );

        return [result, () => setCnt(cnt + 1), cnt];
    }


    return (
        <CacheableQueryProviderContext.Provider value={{
            cacheableQuery: cacheableQuery,
            cacheablePromise: cachablePromise
        }}>
            {props.children}
        </CacheableQueryProviderContext.Provider>
    );
}
